<script>
import { mapState, mapActions } from 'vuex';
import TransportationTableInputs from '../../components/transportation/TransportationTableInputs';
import TransportationCarTypesAdd from '../../components/transportation/TransportationCarTypesAdd';
import TransportationCarTypesEdit from '../../components/transportation/TransportationCarTypesEdit';
import ConfirmDialog from '../../components/TwConfirmDialog';

export default {
  name: 'TransportationCarType',
  components: {
    TransportationTableInputs,
    TransportationCarTypesAdd,
    TransportationCarTypesEdit,
    ConfirmDialog,
  },
  computed: {
    ...mapState({
      statePagination: state => state.transportation.carTypes.pagination,
      carType: state => state.transportation.carTypes.carType,
      carTypes: state => state.transportation.carTypes.items,
      itemsError: state => state.transportation.carTypes.itemsError,
      itemsLoading: state => state.transportation.carTypes.itemsLoading,
      search: state => state.transportation.carTypes.search,
      saveError: state => state.transportation.carTypes.saveError,
      saveLoading: state => state.transportation.carTypes.saveLoading,
      lastSupplier: state => state.transportation.carTypes.supplier,
      supplier: state => state.transportation.suppliers.supplier,
      suppliers: state => state.transportation.suppliers.items,
    }),
  },
  methods: {
    ...mapActions({
      searchCarTypes: 'transportation/carTypesSearch',
      loadCarTypesAction: 'transportation/carTypesLoad',
      updatePagination: 'transportation/carTypesUpdatePagination',
      setActiveCarType: 'transportation/carTypesSetActive',
    }),
    getIndexNumber(index) {
      return index + 1 + ((this.pagination.page - 1) * this.pagination.rowsPerPage);
    },
    changePage(page) {
      this.pagination.page = page;
    },
    loadCarTypes(force = false) {
      if (force || this.supplier !== this.lastSupplier) {
        this.loadCarTypesAction()
          .then(() => {
            this.pagination.totalItems = this.carTypes.length;
          });
      }
    },
    openAddCarType() {
      this.addShow = true;
    },
    closeAddConfirm(force = false) {
      if (!force) {
        this.$refs.addConfirm.open();
      } else {
        this.addShow = false;
      }
    },
    openEditCarType(carType) {
      if (!this.carType || this.carType.id !== carType.id) {
        this.setActiveCarType(carType);
      }
      this.editShow = true;
    },
    closeEditConfirm(force = false) {
      if (this.changesMade && !force) {
        this.$refs.confirm.open();
      } else {
        this.editShow = false;
      }
    },
    formatDistance(distance) {
      return `${distance} ${this.$t('measures.cm')}`;
    },
  },
  data() {
    return {
      headers: [
        { text: '#', sortable: false, width: '30px', class: 'px-2', value: '' },
        { text: this.$t('transportation.carTypes.table.name'), class: 'px-2', value: 'name' },
        { text: this.$t('transportation.carTypes.table.webName'), class: 'px-2', value: 'webName' },
        { text: this.$t('transportation.carTypes.table.length'), class: 'px-2', value: 'length' },
        { text: this.$t('transportation.carTypes.table.height'), class: 'px-2', value: 'height' },
        { text: this.$t('transportation.carTypes.table.externalRef'), class: 'px-2', value: 'externalRef' },
        { text: this.$t('transportation.carTypes.table.active'), width: '70px', class: 'px-2', value: 'active' },
        {
          text: this.$t('transportation.carTypes.table.actions'),
          width: '70px',
          sortable: false,
          class: 'px-2',
          value: '',
        },
      ],
      pagination: {},
      addShow: false,
      editShow: false,
      changesMade: false,
      confirm: {
        title: this.$t('dialog.cancel.title'),
        description: this.$t('dialog.cancel.text'),
      },
    };
  },
  created() {
    this.pagination = Object.assign(this.pagination, this.statePagination);
    this.loadCarTypes();
  },
  beforeDestroy() {
    this.updatePagination(this.pagination);
  },
};
</script>

<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs6>
        <transportation-table-inputs
          @change="loadCarTypes"
          @search="searchCarTypes"
          :disabled="itemsLoading"
          :search="search"
        />
      </v-flex>
      <v-flex xs3 offset-xs3 class="text-xs-right">
        <v-btn
          @click="openAddCarType"
          :disabled="!supplier"
          color="primary"
        >
          Add new type
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout mt-3 row>
      <v-flex>
        <v-data-table
          :items="carTypes"
          :headers="headers"
          :loading="itemsLoading"
          :pagination.sync="pagination"
          :search="search"
          :no-results-text="$t('transportation.carTypes.noResults')"
          :no-data-text="$t('transportation.carTypes.noData')"
          hide-actions
          must-sort
        >
          <template slot="items" slot-scope="props">
            <tr :class="{ 'blue lighten-5': carType && props.item.id === carType.id }">
              <td class="px-2 pr-4">{{ getIndexNumber(props.index) }}</td>
              <td class="px-2">{{ props.item.name }}</td>
              <td class="px-2">{{ props.item.webName }}</td>
              <td class="px-2">{{ formatDistance(props.item.length) }}</td>
              <td class="px-2">{{ formatDistance(props.item.height) }}</td>
              <td class="px-2">{{ props.item.externalRef }}</td>
              <td class="px-2">
                <v-icon v-if="props.item.active" color="green">done</v-icon>
                <v-icon v-else color="red">close</v-icon>
              </td>
              <td class="px-2">
                <v-icon small class="mr-2" @click="openEditCarType(props.item)">
                  edit
                </v-icon>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <div v-if="itemsError">
              <v-alert :value="true" icon="warning">
                {{ $t('transportation.carTypes.actions.load.error') }}.
                <span class="refresh-text" @click="loadCarTypes(true)">
                  {{ $t('inputs.misc.tryAgain') }}
                </span>
              </v-alert>
            </div>
            <div class="text-xs-center" v-else-if="!supplier">
              <span>{{ $t('transportation.suppliers.choose') }}</span>
            </div>
            <div class="text-xs-center" v-else-if="!itemsLoading">
              <span>{{ $t('transportation.carTypes.noData') }}</span>
            </div>
            <div class="text-xs-center" v-else>
              <!--Show nothing when loading-->
            </div>
          </template>
        </v-data-table>
      </v-flex>

      <v-dialog
        v-model="editShow"
        @keydown.esc="editShow = false"
        max-width="600px"
        no-click-animation
        persistent>
        <transportation-car-types-edit
          v-model="changesMade"
          @close="closeEditConfirm"
          :shown="editShow"
        />
      </v-dialog>

      <v-dialog
        v-model="addShow"
        @keydown.esc="addShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-car-types-add
          @close="closeAddConfirm"
          :shown="editShow"
        />
      </v-dialog>

      <confirm-dialog
        @confirm="addShow = false"
        title="Cancel add car type"
        description="Are you sure you want to cancel adding a new car type?"
        ref="addConfirm"
      />

      <confirm-dialog
        @confirm="editShow = false"
        :title="confirm.title"
        :description="confirm.description"
        ref="confirm"
      />
    </v-layout>

    <v-layout justify-center mt-3 row v-if="carTypes">
      <v-flex shrink>
        <v-pagination
          @input="changePage"
          :length="Math.ceil(pagination.totalItems / pagination.rowsPerPage)"
          :value="pagination.page"
          total-visible="10"
        >
        </v-pagination>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style type="scss">
  .v-messages {
    min-height: 14px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/transportation/TransportationCarTypes.vue